import { format, subHours } from "date-fns";
import { useCallback, useEffect, useMemo } from "react";
import {
  getPDA1010_R01DetailList,
  getPDA1010_R01List,
} from "./PDA1010_R01Api";
import usePDA1010_R01Store from "./Store_PDA1010_R01";

const DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
/** 화면이 활성화된 동안 최신 상태를 다시 조회하는 주기입니다. */
const PDA1010_R01_REFETCH_INTERVAL = 20_000;

/** 목록·상세 조회와 카드 선택 등 PDA1010_R01 화면 동작을 담당합니다. */
export const usePDA1010_R01Content = (menuCd?: string) => {
  const PDA1010_R01List = usePDA1010_R01Store(
    (state) => state.PDA1010_R01List
  );
  const PDA1010_R01DetailList = usePDA1010_R01Store(
    (state) => state.PDA1010_R01DetailList
  );
  const selectedPDA1010_R01CardCode = usePDA1010_R01Store(
    (state) => state.selectedPDA1010_R01CardCode
  );
  const isLoading = usePDA1010_R01Store((state) => state.isLoading);
  const setPDA1010_R01List = usePDA1010_R01Store(
    (state) => state.setPDA1010_R01List
  );
  const setPDA1010_R01DetailList = usePDA1010_R01Store(
    (state) => state.setPDA1010_R01DetailList
  );
  const setSelectedPDA1010_R01CardCode = usePDA1010_R01Store(
    (state) => state.setSelectedPDA1010_R01CardCode
  );
  const setIsLoading = usePDA1010_R01Store((state) => state.setIsLoading);

  /** Store의 카드 목록에서 현재 선택된 카드를 계산합니다. */
  const selectedCard = useMemo(
    () =>
      PDA1010_R01List.find(
        (card) => card.cardCode === selectedPDA1010_R01CardCode
      ),
    [PDA1010_R01List, selectedPDA1010_R01CardCode]
  );

  /** 백엔드 카드 목록을 groupName 기준으로 화면 영역별 분류합니다. */
  const getPDA1010_R01GroupList = useCallback(
    (groupName: string) =>
      PDA1010_R01List.filter((card) => card.groupName === groupName),
    [PDA1010_R01List]
  );

  useEffect(() => {
    // 다른 메뉴가 선택된 상태에서는 조회 타이머를 시작하지 않습니다.
    if (menuCd !== "PDA1010_R01") {
      return;
    }

    let isMounted = true;
    // 네트워크 지연 시 20초 주기 요청이 중복 실행되는 것을 방지합니다.
    let isFetching = false;

    const fetchPDA1010_R01List = async (shouldShowLoading: boolean) => {
      if (isFetching) {
        return;
      }

      isFetching = true;
      if (shouldShowLoading) {
        setIsLoading(true);
      }

      try {
        const response = await getPDA1010_R01List();
        if (isMounted) {
          setPDA1010_R01List(response.data.data ?? []);
        }
      } catch {
        // 주기 조회 실패 시 기존 측정값을 유지한다.
      } finally {
        isFetching = false;
        if (isMounted && shouldShowLoading) {
          setIsLoading(false);
        }
      }
    };

    // 메뉴에 진입하거나 다시 선택하면 즉시 최신값을 조회합니다.
    void fetchPDA1010_R01List(true);

    // 최초 조회 이후에는 로딩 화면을 다시 표시하지 않고 값을 갱신합니다.
    const intervalId = window.setInterval(() => {
      void fetchPDA1010_R01List(false);
    }, PDA1010_R01_REFETCH_INTERVAL);

    return () => {
      // 화면 이탈 후 완료된 요청이 Store를 갱신하지 않도록 하고 타이머를 해제합니다.
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [menuCd, setIsLoading, setPDA1010_R01List]);

  /** 카드를 선택하면 최근 24시간 이력을 조회하고 차트 화면으로 전환합니다. */
  const handleSensorClick = useCallback(
    async (cardCode: string) => {
      const endDateTime = new Date();
      const startDateTime = subHours(endDateTime, 24);

      setSelectedPDA1010_R01CardCode(cardCode);
      setPDA1010_R01DetailList([]);
      setIsLoading(true);

      try {
        const response = await getPDA1010_R01DetailList(
          cardCode,
          format(startDateTime, DATE_TIME_FORMAT),
          format(endDateTime, DATE_TIME_FORMAT)
        );
        setPDA1010_R01DetailList(response.data.data ?? []);
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      setPDA1010_R01DetailList,
      setSelectedPDA1010_R01CardCode,
    ]
  );

  /** 상세 차트에서 목록으로 돌아가면서 최신 카드 값을 다시 조회합니다. */
  const handleDetailBack = useCallback(async () => {
    setSelectedPDA1010_R01CardCode(undefined);
    setPDA1010_R01DetailList([]);
    setIsLoading(true);

    try {
      const response = await getPDA1010_R01List();
      setPDA1010_R01List(response.data.data ?? []);
    } finally {
      setIsLoading(false);
    }
  }, [
    setIsLoading,
    setPDA1010_R01DetailList,
    setPDA1010_R01List,
    setSelectedPDA1010_R01CardCode,
  ]);

  return {
    growRoomList: getPDA1010_R01GroupList("재배실"),
    packingList: getPDA1010_R01GroupList("포장 작업장"),
    waterList: getPDA1010_R01GroupList("용수"),
    coldStorageList: getPDA1010_R01GroupList("저온창고"),
    selectedCard,
    detailList: PDA1010_R01DetailList,
    isLoading,
    handleSensorClick,
    handleDetailBack,
  };
};
